import React, { useState, useEffect, useRef, useSyncExternalStore } from "react"
import { navigate } from "gatsby"
import Taskbar from "./Taskbar"
import IconGrid from "./IconGrid"
import DesktopMenu from "./DesktopMenu"
import Window from "./Window"
import { useIsDesktop, DESKTOP_QUERY } from "../hooks/useIsDesktop"
import { useIsEmbedded, readEmbeddedNow } from "../hooks/useIsEmbedded"
import { windowTitleFor } from "../utils/windowTitle"
import {
  subscribe,
  getOrder,
  getServerOrder,
  restoreOrder,
} from "../utils/iconOrder"
import "../styles/desktop-shell.css"

/* How far each new window steps down and right from the last, and how many
   steps before starting over. Eight is about as far as a cascade can run
   before the newest window is off in a corner on a laptop screen. */
const CASCADE_STEP = 30
const CASCADE_SLOTS = 8

/* Where the next window opens.
 *
 * Every window needs a box of its own from the moment it appears. Without one
 * it is a flex item in the desk surface, and flex items do not overlap: five
 * windows share the column five ways and each becomes a 157px sliver with a
 * title bar and almost nothing else. Cascading is the visible half of the fix;
 * being positioned at all is the important half. */
const cascadeBox = (surface, index) => {
  if (!surface || !surface.width) return null

  const width = Math.round(Math.min(Math.max(surface.width * 0.62, 520), 940))
  const height = Math.round(Math.min(Math.max(surface.height * 0.62, 340), 640))
  const slot = index % CASCADE_SLOTS
  const run = (CASCADE_SLOTS - 1) * CASCADE_STEP

  // Start far enough in that the whole run still fits, and far enough right
  // that the first window is not sitting on the icon column.
  const baseX = Math.max(16, Math.min(150, surface.width - width - run))
  const baseY = Math.max(12, Math.min(40, surface.height - height - run))

  return {
    x: baseX + slot * CASCADE_STEP,
    y: baseY + slot * CASCADE_STEP,
    w: width,
    h: height,
  }
}

/**
 * The desktop metaphor, as a skin over routing.
 *
 * Home is the desktop itself: wallpaper and icons, no window. Every other
 * route opens inside window chrome. Routing is untouched either way, so each
 * page is still its own Gatsby route with its own URL and static HTML.
 *
 * More than one window can be open, but only ever one of them is the route.
 * That one renders the real page and owns the address bar; the rest load their
 * own route in a frame. So the URL always names something real and a link to
 * it opens exactly what it says, however many windows are stacked on top.
 *
 * Mounts from an effect gated on viewport width, so mobile and the SSR output
 * render the page tree exactly as they did before any of this existed.
 */
const DesktopShell = ({ location, theme, toggleTheme, children }) => {
  const isDesktop = useIsDesktop()
  const isEmbedded = useIsEmbedded()
  const [deskMenu, setDeskMenu] = useState(null)

  /* Extra windows, in the order they were opened. The route's own window is
     not in here: it comes and goes with navigation and is always drawn first.
     Shaded state lives up here because the taskbar draws a button per window
     and has to know. Everything else about a window is the window's business. */
  const [frames, setFrames] = useState([])
  const [routeShaded, setRouteShaded] = useState(false)

  /* Which window is on top. "route" means the route's own window. */
  const [focused, setFocused] = useState("route")
  const nextId = useRef(1)

  const surfaceRef = useRef(null)

  // The window body becomes the scroll container, so the document behind it
  // must not scroll too. Only while the shell is actually mounted.
  //
  // desktop-mode is usually already here: gatsby-ssr.js adds it before first
  // paint so none of the desktop styling arrives late. This still owns it,
  // because a resize can take the class back off. shell-ready is the other
  // half of that handshake, and says the page tree is now in a window, so the
  // CSS can stop holding the phone markup back.
  // Both halves have to ignore `isDesktop` on the first client render, where it
  // is false whatever the screen is: it starts false so hydration matches the
  // server, and useIsDesktop only reports the truth a render later. Trusting it
  // here strips the desktop-mode the pre-paint script set and adds shell-ready
  // in the same breath, which lifts the guard hiding the phone markup at the
  // one moment there is no window to put in its place. That is the flash this
  // handshake exists to prevent.
  //
  // So the class comes off the media query, which is never stale, and
  // shell-ready waits until the shell is genuinely rendered. Off the desktop
  // no window is coming, nothing is being held back, and it can go on at once.
  useEffect(() => {
    const root = document.documentElement
    /* Embedded, the wallpaper and the scroll lock belong to the parent
       document, not to this frame's. The classes are cleared rather than left
       alone: desktop-mode carries the rule that hides the page until a shell
       reports for duty, and no shell is coming here. The pre-paint script
       already skips it on a cold load; this covers a client-side navigation
       that arrives embedded. */
    if (readEmbeddedNow()) {
      root.classList.remove("desktop-mode")
      root.classList.add("shell-ready")
      return undefined
    }

    const matchesDesktop = window.matchMedia(DESKTOP_QUERY).matches

    root.classList.toggle("desktop-mode", matchesDesktop)
    if (isDesktop || !matchesDesktop) {
      root.classList.add("shell-ready")
    }

    // After mount, like every other bit of remembered state here, so the
    // server markup and the first client render agree on the authored order.
    if (isDesktop) restoreOrder()

    return () => {
      root.classList.remove("desktop-mode")
      root.classList.remove("shell-ready")
    }
  }, [isDesktop])

  // Reopen a shaded window on navigation, otherwise the next page loads rolled
  // up. The About box goes with it: it describes one app, so carrying it over
  // to the next route would leave it describing the wrong one.
  /* Navigation replaces what the route window is showing, so it opens back up
     and takes focus. The frames are untouched: they are their own routes and
     have nothing to do with where the address bar went. */
  useEffect(() => {
    setRouteShaded(false)
    setDeskMenu(null)
    setFocused("route")
  }, [location?.pathname, location?.search])

  /* Above the early return, with the other hooks: this component bails out to
     bare children off the desktop, and a hook called after that point changes
     the hook count between renders the moment the viewport crosses over. */
  const iconOrder = useSyncExternalStore(subscribe, getOrder, getServerOrder)

  /* An embedded page is already inside somebody's window. Building another
     desktop in there would nest a taskbar and a wallpaper inside a window on
     the taskbar and wallpaper you are looking at. */
  if (isEmbedded || !isDesktop) {
    return children
  }

  const isDesktopRoot = (location?.pathname || "/") === "/"
  const title = windowTitleFor(location)
  const route = (location?.pathname || "/").replace(/\/+$/, "") || "/"

  /* Opening something while a window is already up puts it in a new window
     rather than replacing what you are looking at, which is the whole point of
     having more than one. On the bare desktop there is nothing to preserve, so
     that first one is a normal navigation and becomes the route window.

     Returns whether it handled the click, so the caller knows whether to let
     the link navigate. */
  const openWindow = target => {
    const clean = target.replace(/\/+$/, "") || "/"
    if (clean === "/") return false
    if (isDesktopRoot) return false

    // Already open somewhere: raise it rather than opening a second copy.
    if (clean === route) {
      setRouteShaded(false)
      setFocused("route")
      return true
    }

    const existing = frames.find(f => f.route === clean)
    if (existing) {
      setFrames(prev =>
        prev.map(f => (f.id === existing.id ? { ...f, shaded: false } : f))
      )
      setFocused(existing.id)
      return true
    }

    const id = `frame-${nextId.current}`
    nextId.current += 1
    setFrames(prev => [
      ...prev,
      {
        id,
        route: clean,
        title: windowTitleFor({ pathname: clean }),
        shaded: false,
        // Measured now, against the surface as it currently is, so the box is
        // right for the screen the window is actually opening on.
        box: cascadeBox(
          surfaceRef.current?.getBoundingClientRect(),
          prev.length
        ),
      },
    ])
    setFocused(id)
    return true
  }

  /* Clicks on anything that opens a route, wherever it is: the icons behind
     the window, the start menu, a link inside a window. Kept as one handler on
     the shell rather than threaded through every component, and deliberately
     built on real anchors: without JS, or for a crawler, these are ordinary
     links to ordinary pages and always were. */
  const onShellClick = e => {
    // Let the browser have modified clicks: new tab, new window, download.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)
      return

    const anchor = e.target.closest("a[href]")
    if (!anchor) return
    if (anchor.target === "_blank") return

    /* A link inside a window navigates that window, it does not spawn another.
       Clicking a post in the list should show the post where the list was,
       the way an open folder does. Only the desktop and the start menu open
       something new. A link inside a framed window never reaches here at all:
       that click happens in the frame's own document and moves the frame. */
    if (anchor.closest(".win-body")) return

    const url = new URL(anchor.href, window.location.origin)
    if (url.origin !== window.location.origin) return
    // A query means a filtered list, which is a different thing at the same
    // path. Let those navigate normally.
    if (url.search) return

    if (openWindow(url.pathname)) e.preventDefault()
  }

  const closeFrame = id => {
    setFrames(prev => prev.filter(f => f.id !== id))
    setFocused(f => (f === id ? "route" : f))
  }

  const toggleFrameShade = id => {
    setFrames(prev =>
      prev.map(f => (f.id === id ? { ...f, shaded: !f.shaded } : f))
    )
    setFocused(id)
  }

  /* Only on the wallpaper. Inside a window the browser's own menu has to
     survive, or the terminal loses paste and every app loses open-in-new-tab. */
  const onDeskContextMenu = e => {
    if (e.target.closest(".win")) return
    e.preventDefault()
    setDeskMenu({ x: e.clientX, y: e.clientY })
  }
  /* Focus, healed rather than stored. Closing the route window navigates home,
     which leaves `focused` naming a window that no longer exists, and every
     remaining window then draws as inactive with none of them on top. So the
     stored value is honoured only while it still refers to something, and
     otherwise the topmost window takes it. */
  const hasRouteWindow = !isDesktopRoot
  const focusIsReal =
    (focused === "route" && hasRouteWindow) ||
    frames.some(f => f.id === focused)
  const activeId = focusIsReal
    ? focused
    : frames.length
    ? frames[frames.length - 1].id
    : hasRouteWindow
    ? "route"
    : null

  /* Stacking order. The focused window goes on top; the rest keep the order
     they were opened in, so a stack does not reshuffle itself every time you
     click one. */
  const zFor = id =>
    activeId === id ? 30 : 10 + frames.findIndex(f => f.id === id)

  const taskbarWindows = [
    ...(isDesktopRoot ? [] : [{ id: "route", title, shaded: routeShaded }]),
    ...frames.map(f => ({ id: f.id, title: f.title, shaded: f.shaded })),
  ]

  /* Captured on the shell rather than the surface so the start menu is covered
     too: it lives in the taskbar, outside the desk surface, and a menu item
     that slipped past this would replace the window you were looking at
     instead of opening a new one. */
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="desktop-shell" onClickCapture={onShellClick}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className="desk-surface"
        ref={surfaceRef}
        onContextMenu={onDeskContextMenu}
      >
        {/* The icons belong to the desktop, not to the home route, so they stay
            visible behind whatever is open. On the bare desktop the home route
            draws them itself, as its own content. */}
        {isDesktopRoot ? (
          children
        ) : (
          <div className="desktop-icons desk-icon-layer">
            <IconGrid />
          </div>
        )}

        {!isDesktopRoot && (
          <Window
            title={title}
            route={route}
            surfaceRef={surfaceRef}
            isShaded={routeShaded}
            onToggleShade={() => setRouteShaded(v => !v)}
            onClose={() => navigate("/")}
            onFocus={() => setFocused("route")}
            isFocused={activeId === "route"}
            zIndex={zFor("route")}
          >
            {children}
          </Window>
        )}

        {frames.map(frame => (
          <Window
            key={frame.id}
            embedded
            title={frame.title}
            route={frame.route}
            surfaceRef={surfaceRef}
            isShaded={frame.shaded}
            onToggleShade={() => toggleFrameShade(frame.id)}
            onClose={() => closeFrame(frame.id)}
            onFocus={() => setFocused(frame.id)}
            isFocused={activeId === frame.id}
            zIndex={zFor(frame.id)}
            initialBox={frame.box}
          />
        ))}
      </div>

      {deskMenu && (
        <DesktopMenu
          x={deskMenu.x}
          y={deskMenu.y}
          current={iconOrder}
          onClose={() => setDeskMenu(null)}
        />
      )}

      <Taskbar
        windows={taskbarWindows}
        focused={activeId}
        onWindowButton={id => {
          if (id === "route") {
            setRouteShaded(v => (activeId === "route" ? !v : false))
            setFocused("route")
            return
          }
          toggleFrameShade(id)
        }}
        theme={theme}
        toggleTheme={toggleTheme}
      />
    </div>
  )
}

export default DesktopShell
